import {
  Component,
  ContentChild,
  Directive,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/** Marks projected content as the pill button's left icon: <svg pillIcon ...> */
@Directive({
  selector: '[pillIcon]',
  standalone: true,
})
export class PillIconDirective {}

const PARCHMENT = ['var(--color-parchment)', '#f2eadf'];
const EVERGREEN = ['var(--color-evergreen)', '#2c3a32'];

/**
 * Pill-shaped button for the new UI (BCAD-534). All six design variants
 * (one/two lines x left icon/none x solid/gradient) are driven by inputs.
 * Ported from the Make-generated React PillButton.
 */
@Component({
  selector: 'app-pill-button',
  templateUrl: './pill-button.component.html',
  styleUrls: ['./pill-button.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class PillButtonComponent {
  @Input() title = '';
  @Input() subtitle?: string;
  @Input() size: 'micro' | 'xs' | 'compact' | 'small' | 'medsmall' | 'medium' | 'large' | 'xl' = 'large';

  @Input() backgroundColor = 'var(--color-evergreen)';
  @Input() gradientFrom?: string;
  @Input() gradientTo?: string;
  @Input() textColor = 'var(--color-parchment)';

  @Input() disabled = false;
  /** Applies the disabled look without setting the native `disabled` attribute, so `(clicked)` still fires. */
  @Input() visuallyDisabled = false;

  @Input() showForwardIcon = true;
  @Input() forwardIconColor = 'var(--color-parchment)';
  @Input() forwardArrowColor = '';

  @Output() clicked = new EventEmitter<void>();

  @ContentChild(PillIconDirective) leftIcon?: PillIconDirective;

  get hasLeftIcon(): boolean {
    return !!this.leftIcon;
  }

  get background(): string {
    return this.gradientFrom && this.gradientTo
      ? `linear-gradient(to right, ${this.gradientFrom}, ${this.gradientTo})`
      : this.backgroundColor;
  }

  /** Hover swaps the forward circle between parchment and evergreen; any other colour stays. */
  get forwardIconHoverColor(): string {
    if (PARCHMENT.includes(this.forwardIconColor)) {
      return 'var(--color-evergreen)';
    }
    if (EVERGREEN.includes(this.forwardIconColor)) {
      return 'var(--color-parchment)';
    }
    return this.forwardIconColor;
  }

  private computeArrowContrast(circleColor: string): string {
    if (PARCHMENT.includes(circleColor)) return 'var(--color-evergreen)';
    if (EVERGREEN.includes(circleColor)) return 'var(--color-parchment)';
    return 'var(--color-parchment)';
  }

  get forwardArrowColorValue(): string {
    return this.forwardArrowColor || this.computeArrowContrast(this.forwardIconColor);
  }

  get forwardArrowHoverColor(): string {
    return this.computeArrowContrast(this.forwardIconHoverColor);
  }

  get forwardIconSrc(): string {
    return PARCHMENT.includes(this.forwardIconColor)
      ? 'assets/icons/icon-forward-with-circle.svg'
      : 'assets/icons/icon-forward-with-circle-evergreen.svg';
  }
}
